import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft, Save, Megaphone, Calendar, Target, DollarSign, Upload, Image,
  FileText, Video, Trash2, Plus, Loader2, CheckCircle2, AlertCircle,
  Users, Globe, Mail, Facebook, Instagram, Linkedin, Twitter, BarChart3,
  Palette, Eye, Link as LinkIcon, X, GripVertical, Sparkles
} from 'lucide-react';
import api from '../api';
import { AIContentGenerator } from '../components/AIContentGenerator';

const CAMPAIGN_TYPES = [
  { value: 'email', label: 'Email Campaign', icon: Mail, description: 'Email marketing campaigns and newsletters' },
  { value: 'social_media', label: 'Social Media', icon: Facebook, description: 'Social media posts and engagement' },
  { value: 'paid_ads', label: 'Paid Advertising', icon: DollarSign, description: 'PPC, display ads, and sponsored content' },
  { value: 'event', label: 'Event Marketing', icon: Calendar, description: 'Webinars, conferences, and virtual events' },
  { value: 'content', label: 'Content Marketing', icon: FileText, description: 'Blogs, whitepapers, and educational content' },
  { value: 'multi_channel', label: 'Multi-Channel', icon: Megaphone, description: 'Integrated campaigns across multiple channels' }
];

const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter },
  { value: 'tiktok', label: 'TikTok', icon: Video },
  { value: 'google_ads', label: 'Google Ads', icon: Globe },
  { value: 'meta_ads', label: 'Meta Ads', icon: Facebook },
  { value: 'linkedin_ads', label: 'LinkedIn Ads', icon: Linkedin },
  { value: 'website', label: 'Website', icon: Globe },
  { value: 'youtube', label: 'YouTube', icon: Video },
  { value: 'sms', label: 'SMS', icon: Mail }
];

const ASSET_TYPES = [
  { value: 'image', label: 'Image', icon: Image, accept: 'image/*' },
  { value: 'video', label: 'Video', icon: Video, accept: 'video/*' },
  { value: 'document', label: 'Document', icon: FileText, accept: '.pdf,.doc,.docx,.ppt,.pptx' },
  { value: 'creative', label: 'Creative/Design', icon: Palette, accept: 'image/*,.psd,.ai,.sketch' }
];

const CONTENT_TYPES = [
  { value: 'messaging', label: 'Messaging / Copy', icon: Mail, description: 'Email copy, ad text, social posts' },
  { value: 'support_docs', label: 'Support Documentation', icon: FileText, description: 'Help articles, FAQs, guides' },
  { value: 'landing_page', label: 'Landing Page', icon: Globe, description: 'Landing page content and structure' },
  { value: 'blog_post', label: 'Blog Post', icon: FileText, description: 'Blog articles and content' },
  { value: 'social_post', label: 'Social Media Post', icon: Twitter, description: 'Social media content' },
  { value: 'video_script', label: 'Video Script', icon: Video, description: 'Video scripts and storyboards' },
  { value: 'press_release', label: 'Press Release', icon: FileText, description: 'PR and news content' },
  { value: 'email_template', label: 'Email Template', icon: Mail, description: 'Email templates and variants' }
];

export const CampaignEditPage = () => {
  const { campaignId } = useParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const isNew = !campaignId || campaignId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [segments, setSegments] = useState([]);
  const [productLines, setProductLines] = useState([]);
  
  // Form state
  const [campaign, setCampaign] = useState({
    name: '',
    description: '',
    campaign_type: 'email',
    channels: [],
    status: 'draft',
    
    // Scheduling
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    timezone: 'America/New_York',
    
    // Budget
    budget: '',
    budget_spent: 0,
    expected_roi: '',
    cost_per_lead: '',
    
    // Targeting
    target_segments: [],
    target_product_lines: [],
    target_audience_size: '',
    target_demographics: {
      age_range: '',
      locations: [],
      interests: []
    },
    
    // Goals & KPIs
    goals: [],
    kpis: {
      target_impressions: '',
      target_clicks: '',
      target_conversions: '',
      target_leads: ''
    },
    
    // Content
    headline: '',
    tagline: '',
    call_to_action: '',
    landing_page_url: '',
    content_items: [], // Multiple content types (messaging, support docs, etc.)
    utm_params: {
      source: '',
      medium: '',
      campaign: '',
      term: '',
      content: ''
    },
    
    // Assets
    assets: [],
    
    // Tags
    tags: [],
    
    // Notes
    internal_notes: ''
  });
  
  // Asset upload state
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [assetDialog, setAssetDialog] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: '',
    type: 'image',
    description: '',
    file: null,
    url: ''
  });
  
  // Content item dialog state
  const [contentItemDialog, setContentItemDialog] = useState(false);
  const [editingContentItem, setEditingContentItem] = useState(null);
  const [newContentItem, setNewContentItem] = useState({
    id: '',
    content_type: 'messaging',
    title: '',
    body: '',
    channel: '',
    variant: '',
    notes: ''
  });
  
  // AI Content Generator
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiGeneratorField, setAIGeneratorField] = useState('headline');
  
  // Tag input
  const [tagInput, setTagInput] = useState('');
  
  // Goal input
  const [goalInput, setGoalInput] = useState('');

  useEffect(() => {
    fetchReferenceData();
    if (!isNew) {
      fetchCampaign();
    }
  }, [campaignId]);

  const fetchReferenceData = async () => {
    try {
      const [segmentsRes, productLinesRes] = await Promise.all([
        api.get('/segments').catch(() => ({ data: { segments: [] } })),
        api.get('/product-lines').catch(() => ({ data: [] }))
      ]);
      // Handle different response formats
      const segmentsData = segmentsRes.data?.segments || segmentsRes.data || [];
      const productLinesData = productLinesRes.data || [];
      setSegments(Array.isArray(segmentsData) ? segmentsData : []);
      setProductLines(Array.isArray(productLinesData) ? productLinesData : []);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
      setSegments([]);
      setProductLines([]);
    }
  };

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/marketing/campaigns/${campaignId}`);
      setCampaign(prev => ({
        ...prev,
        ...res.data,
        budget: res.data.budget?.toString() || '',
        expected_roi: res.data.expected_roi?.toString() || '',
        cost_per_lead: res.data.cost_per_lead?.toString() || '',
        kpis: {
          target_impressions: res.data.kpis?.target_impressions?.toString() || '',
          target_clicks: res.data.kpis?.target_clicks?.toString() || '',
          target_conversions: res.data.kpis?.target_conversions?.toString() || '',
          target_leads: res.data.kpis?.target_leads?.toString() || ''
        },
        target_demographics: res.data.target_demographics || { age_range: '', locations: [], interests: [] },
        utm_params: res.data.utm_params || { source: '', medium: '', campaign: '', term: '', content: '' }
      }));
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
      toast.error('Failed to load campaign');
      navigate('/marketing/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!campaign.name.trim()) {
      toast.error('Campaign name is required');
      setActiveTab('basic');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...campaign,
        budget: campaign.budget ? parseFloat(campaign.budget) : null,
        expected_roi: campaign.expected_roi ? parseFloat(campaign.expected_roi) : null,
        cost_per_lead: campaign.cost_per_lead ? parseFloat(campaign.cost_per_lead) : null,
        kpis: {
          target_impressions: campaign.kpis.target_impressions ? parseInt(campaign.kpis.target_impressions) : null,
          target_clicks: campaign.kpis.target_clicks ? parseInt(campaign.kpis.target_clicks) : null,
          target_conversions: campaign.kpis.target_conversions ? parseInt(campaign.kpis.target_conversions) : null,
          target_leads: campaign.kpis.target_leads ? parseInt(campaign.kpis.target_leads) : null
        }
      };
      
      if (isNew) {
        const res = await api.post('/marketing/campaigns', payload);
        toast.success('Campaign created successfully');
        navigate(`/marketing/campaigns/${res.data.id}`);
      } else {
        await api.put(`/marketing/campaigns/${campaignId}`, payload);
        toast.success('Campaign updated successfully');
        navigate(`/marketing/campaigns/${campaignId}`);
      }
    } catch (error) {
      console.error('Failed to save campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleAssetUpload = async () => {
    if (!newAsset.file && !newAsset.url) {
      toast.error('Please select a file or enter a URL');
      return;
    }

    try {
      setUploadingAsset(true);
      
      let assetUrl = newAsset.url;
      
      if (newAsset.file) {
        const formData = new FormData();
        formData.append('file', newAsset.file);
        
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        assetUrl = uploadRes.data.url;
      }
      
      const asset = {
        id: Date.now().toString(),
        name: newAsset.name || newAsset.file?.name || 'Untitled Asset',
        type: newAsset.type,
        description: newAsset.description,
        url: assetUrl,
        file_size: newAsset.file?.size || null,
        created_at: new Date().toISOString()
      };
      
      setCampaign(prev => ({
        ...prev,
        assets: [...(prev.assets || []), asset]
      }));
      
      setAssetDialog(false);
      setNewAsset({ name: '', type: 'image', description: '', file: null, url: '' });
      toast.success('Asset added');
    } catch (error) {
      console.error('Failed to upload asset:', error);
      toast.error('Failed to upload asset');
    } finally {
      setUploadingAsset(false);
    }
  };

  const removeAsset = (assetId) => {
    setCampaign(prev => ({
      ...prev,
      assets: prev.assets.filter(a => a.id !== assetId)
    }));
    toast.success('Asset removed');
  };

  const addTag = () => {
    if (tagInput.trim() && !campaign.tags.includes(tagInput.trim())) {
      setCampaign(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setCampaign(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const addGoal = () => {
    if (goalInput.trim() && !campaign.goals.includes(goalInput.trim())) {
      setCampaign(prev => ({
        ...prev,
        goals: [...prev.goals, goalInput.trim()]
      }));
      setGoalInput('');
    }
  };

  const removeGoal = (goal) => {
    setCampaign(prev => ({
      ...prev,
      goals: prev.goals.filter(g => g !== goal)
    }));
  };

  const toggleChannel = (channelValue) => {
    setCampaign(prev => ({
      ...prev,
      channels: prev.channels.includes(channelValue)
        ? prev.channels.filter(c => c !== channelValue)
        : [...prev.channels, channelValue]
    }));
  };

  const toggleSegment = (segmentId) => {
    setCampaign(prev => ({
      ...prev,
      target_segments: prev.target_segments.includes(segmentId)
        ? prev.target_segments.filter(s => s !== segmentId)
        : [...prev.target_segments, segmentId]
    }));
  };

  const toggleProductLine = (plId) => {
    setCampaign(prev => ({
      ...prev,
      target_product_lines: prev.target_product_lines.includes(plId)
        ? prev.target_product_lines.filter(p => p !== plId)
        : [...prev.target_product_lines, plId]
    }));
  };

  const generateUtmParams = () => {
    setCampaign(prev => ({
      ...prev,
      utm_params: {
        source: prev.channels[0] || 'email',
        medium: prev.campaign_type === 'paid_ads' ? 'cpc' : prev.campaign_type,
        campaign: prev.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        term: '',
        content: ''
      }
    }));
    toast.success('UTM parameters generated');
  };

  // Content item handlers
  const handleAddContentItem = () => {
    setEditingContentItem(null);
    setNewContentItem({
      id: crypto.randomUUID(),
      content_type: 'messaging',
      title: '',
      body: '',
      channel: campaign.channels[0] || 'none',
      variant: '',
      notes: ''
    });
    setContentItemDialog(true);
  };

  const handleEditContentItem = (item) => {
    setEditingContentItem(item);
    setNewContentItem({ 
      ...item,
      channel: item.channel || 'none'  // Convert empty to 'none' for Select
    });
    setContentItemDialog(true);
  };

  const handleSaveContentItem = () => {
    if (!newContentItem.title) {
      toast.error('Content title is required');
      return;
    }
    
    // Convert 'none' back to empty string for storage
    const itemToSave = {
      ...newContentItem,
      channel: newContentItem.channel === 'none' ? '' : newContentItem.channel
    };
    
    if (editingContentItem) {
      setCampaign(prev => ({
        ...prev,
        content_items: prev.content_items.map(item => 
          item.id === editingContentItem.id ? itemToSave : item
        )
      }));
      toast.success('Content item updated');
    } else {
      setCampaign(prev => ({
        ...prev,
        content_items: [...prev.content_items, itemToSave]
      }));
      toast.success('Content item added');
    }
    setContentItemDialog(false);
  };

  const handleDeleteContentItem = (itemId) => {
    setCampaign(prev => ({
      ...prev,
      content_items: prev.content_items.filter(item => item.id !== itemId)
    }));
    toast.success('Content item removed');
  };

  const handleAIContentSelect = (text, generationType) => {
    // Map generation type to campaign field
    switch (generationType) {
      case 'headline':
        setCampaign(prev => ({ ...prev, headline: text }));
        toast.success('Headline applied');
        break;
      case 'tagline':
        setCampaign(prev => ({ ...prev, tagline: text }));
        toast.success('Tagline applied');
        break;
      case 'cta':
        setCampaign(prev => ({ ...prev, call_to_action: text }));
        toast.success('CTA applied');
        break;
      default:
        // For full copy or expanded content, show in a toast and let user copy
        navigator.clipboard.writeText(text);
        toast.success('Content copied to clipboard');
    }
  };

  const openAIGenerator = (field) => {
    setAIGeneratorField(field);
    setShowAIGenerator(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={isNew ? '/marketing/campaigns' : `/marketing/campaigns/${campaignId}`}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">
              {isNew ? 'Create Campaign' : 'Edit Campaign'}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? 'Set up your new marketing campaign' : campaign.name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Link to="/marketing/campaigns">
            <Button variant="outline">Cancel</Button>
          </Link>
          <Button onClick={handleSave} disabled={saving} data-testid="save-campaign-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isNew ? 'Create Campaign' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <Megaphone className="h-4 w-4" />
            <span className="hidden sm:inline">Basic Info</span>
          </TabsTrigger>
          <TabsTrigger value="targeting" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <span className="hidden sm:inline">Targeting</span>
          </TabsTrigger>
          <TabsTrigger value="budget" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">Budget</span>
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Content</span>
          </TabsTrigger>
          <TabsTrigger value="assets" className="flex items-center gap-2">
            <Image className="h-4 w-4" />
            <span className="hidden sm:inline">Assets</span>
          </TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Campaign Details</CardTitle>
                <CardDescription>Basic information about your campaign</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Campaign Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter a descriptive campaign name..."
                    value={campaign.name}
                    onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="campaign-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the campaign objectives, strategy, and expected outcomes..."
                    value={campaign.description}
                    onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label>Campaign Type</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CAMPAIGN_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = campaign.campaign_type === type.value;
                      return (
                        <div
                          key={type.value}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          onClick={() => setCampaign(prev => ({ ...prev, campaign_type: type.value }))}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-white' : 'bg-slate-100'}`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-medium">{type.label}</p>
                              <p className="text-xs text-muted-foreground">{type.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={campaign.start_date}
                      onChange={(e) => setCampaign(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={campaign.end_date}
                      onChange={(e) => setCampaign(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Channels */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Channels</CardTitle>
                  <CardDescription>Select campaign channels</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map((channel) => {
                      const Icon = channel.icon;
                      const isSelected = campaign.channels.includes(channel.value);
                      return (
                        <Badge
                          key={channel.value}
                          variant={isSelected ? 'default' : 'outline'}
                          className="cursor-pointer py-1.5 px-3"
                          onClick={() => toggleChannel(channel.value)}
                        >
                          <Icon className="h-3 w-3 mr-1" />
                          {channel.label}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    />
                    <Button variant="outline" size="icon" onClick={addTag}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {campaign.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {campaign.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="pr-1">
                          {tag}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 ml-1 hover:bg-transparent"
                            onClick={() => removeTag(tag)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Goals */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Campaign Goals</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add goal..."
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                    />
                    <Button variant="outline" size="icon" onClick={addGoal}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {campaign.goals.length > 0 && (
                    <ul className="space-y-2">
                      {campaign.goals.map((goal, idx) => (
                        <li key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            {goal}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeGoal(goal)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Targeting Tab */}
        <TabsContent value="targeting" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Segments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Customer Segments
                </CardTitle>
                <CardDescription>Select target customer segments</CardDescription>
              </CardHeader>
              <CardContent>
                {segments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No segments available</p>
                ) : (
                  <div className="space-y-2">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          campaign.target_segments.includes(segment.id)
                            ? 'border-primary bg-primary/5'
                            : 'hover:border-slate-300'
                        }`}
                        onClick={() => toggleSegment(segment.id)}
                        data-testid={`segment-${segment.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={segment.color || 'bg-slate-100 text-slate-700'}>
                              {segment.label || segment.name || segment.value}
                            </Badge>
                          </div>
                          {campaign.target_segments.includes(segment.id) && (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        {segment.description && (
                          <p className="text-xs text-muted-foreground mt-1">{segment.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Product Lines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Product Lines
                </CardTitle>
                <CardDescription>Select target product lines</CardDescription>
              </CardHeader>
              <CardContent>
                {productLines.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No product lines available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {productLines.map((pl) => (
                      <Badge
                        key={pl.id}
                        variant={campaign.target_product_lines.includes(pl.id) ? 'default' : 'outline'}
                        className="cursor-pointer py-2 px-3"
                        style={{
                          backgroundColor: campaign.target_product_lines.includes(pl.id) ? pl.color : 'transparent',
                          borderColor: pl.color,
                          color: campaign.target_product_lines.includes(pl.id) ? 'white' : pl.color
                        }}
                        onClick={() => toggleProductLine(pl.id)}
                      >
                        {pl.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audience Size */}
            <Card>
              <CardHeader>
                <CardTitle>Estimated Audience</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Target Audience Size</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50000"
                      value={campaign.target_audience_size}
                      onChange={(e) => setCampaign(prev => ({ ...prev, target_audience_size: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Age Range</Label>
                    <Input
                      placeholder="e.g., 25-45"
                      value={campaign.target_demographics.age_range}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        target_demographics: { ...prev.target_demographics, age_range: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPIs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Target KPIs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Impressions</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100000"
                      value={campaign.kpis.target_impressions}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        kpis: { ...prev.kpis, target_impressions: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Clicks</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 5000"
                      value={campaign.kpis.target_clicks}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        kpis: { ...prev.kpis, target_clicks: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Conversions</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 500"
                      value={campaign.kpis.target_conversions}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        kpis: { ...prev.kpis, target_conversions: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Leads</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 200"
                      value={campaign.kpis.target_leads}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        kpis: { ...prev.kpis, target_leads: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Budget Planning
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Total Budget (USD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="pl-9"
                      value={campaign.budget}
                      onChange={(e) => setCampaign(prev => ({ ...prev, budget: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Expected ROI (%)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 150"
                    value={campaign.expected_roi}
                    onChange={(e) => setCampaign(prev => ({ ...prev, expected_roi: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Target Cost Per Lead (USD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="pl-9"
                      value={campaign.cost_per_lead}
                      onChange={(e) => setCampaign(prev => ({ ...prev, cost_per_lead: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Budget Summary */}
            {campaign.budget && (
              <Card>
                <CardHeader>
                  <CardTitle>Budget Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-green-700">Total Budget</span>
                      <span className="text-lg font-bold text-green-900">
                        ${parseFloat(campaign.budget).toLocaleString()}
                      </span>
                    </div>
                    {campaign.expected_roi && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-green-600">Expected Revenue</span>
                        <span className="font-medium text-green-800">
                          ${(parseFloat(campaign.budget) * (1 + parseFloat(campaign.expected_roi) / 100)).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {campaign.kpis.target_leads && campaign.cost_per_lead && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        At ${campaign.cost_per_lead} per lead, your budget can acquire approximately{' '}
                        <span className="font-bold">
                          {Math.floor(parseFloat(campaign.budget) / parseFloat(campaign.cost_per_lead)).toLocaleString()} leads
                        </span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Campaign Content</CardTitle>
                    <CardDescription>Primary messaging and copy</CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openAIGenerator('headline')}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI Generate
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Headline</Label>
                    <Button variant="ghost" size="sm" onClick={() => openAIGenerator('headline')} className="h-6 text-xs text-purple-600">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate
                    </Button>
                  </div>
                  <Input
                    placeholder="Main campaign headline..."
                    value={campaign.headline}
                    onChange={(e) => setCampaign(prev => ({ ...prev, headline: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Tagline / Subheadline</Label>
                    <Button variant="ghost" size="sm" onClick={() => openAIGenerator('tagline')} className="h-6 text-xs text-purple-600">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate
                    </Button>
                  </div>
                  <Input
                    placeholder="Supporting tagline..."
                    value={campaign.tagline}
                    onChange={(e) => setCampaign(prev => ({ ...prev, tagline: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Call to Action</Label>
                    <Button variant="ghost" size="sm" onClick={() => openAIGenerator('cta')} className="h-6 text-xs text-purple-600">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate
                    </Button>
                  </div>
                  <Input
                    placeholder="e.g., Shop Now, Learn More, Sign Up"
                    value={campaign.call_to_action}
                    onChange={(e) => setCampaign(prev => ({ ...prev, call_to_action: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Landing Page URL</Label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="url"
                      placeholder="https://..."
                      className="pl-9"
                      value={campaign.landing_page_url}
                      onChange={(e) => setCampaign(prev => ({ ...prev, landing_page_url: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  UTM Parameters
                  <Button variant="outline" size="sm" onClick={generateUtmParams}>
                    Auto-Generate
                  </Button>
                </CardTitle>
                <CardDescription>Tracking parameters for analytics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Input
                      placeholder="e.g., facebook"
                      value={campaign.utm_params.source}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        utm_params: { ...prev.utm_params, source: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Medium</Label>
                    <Input
                      placeholder="e.g., cpc, email"
                      value={campaign.utm_params.medium}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        utm_params: { ...prev.utm_params, medium: e.target.value }
                      }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Campaign</Label>
                  <Input
                    placeholder="e.g., spring-sale-2026"
                    value={campaign.utm_params.campaign}
                    onChange={(e) => setCampaign(prev => ({
                      ...prev,
                      utm_params: { ...prev.utm_params, campaign: e.target.value }
                    }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Term (optional)</Label>
                    <Input
                      placeholder="Keywords"
                      value={campaign.utm_params.term}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        utm_params: { ...prev.utm_params, term: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Content (optional)</Label>
                    <Input
                      placeholder="Ad variant"
                      value={campaign.utm_params.content}
                      onChange={(e) => setCampaign(prev => ({
                        ...prev,
                        utm_params: { ...prev.utm_params, content: e.target.value }
                      }))}
                    />
                  </div>
                </div>

                {campaign.landing_page_url && campaign.utm_params.source && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Preview URL</Label>
                    <p className="text-xs font-mono break-all mt-1">
                      {campaign.landing_page_url}
                      ?utm_source={campaign.utm_params.source}
                      &utm_medium={campaign.utm_params.medium}
                      &utm_campaign={campaign.utm_params.campaign}
                      {campaign.utm_params.term && `&utm_term=${campaign.utm_params.term}`}
                      {campaign.utm_params.content && `&utm_content=${campaign.utm_params.content}`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Internal Notes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Internal Notes</CardTitle>
                <CardDescription>Notes visible only to your team</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add internal notes, strategy details, or reminders..."
                  value={campaign.internal_notes}
                  onChange={(e) => setCampaign(prev => ({ ...prev, internal_notes: e.target.value }))}
                  rows={4}
                />
              </CardContent>
            </Card>

            {/* Multiple Content Items */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Content Items
                    </CardTitle>
                    <CardDescription>Add multiple content types for your campaign (messaging, support docs, email templates, etc.)</CardDescription>
                  </div>
                  <Button onClick={handleAddContentItem} data-testid="add-content-item-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Content
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {campaign.content_items.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-medium mb-1">No content items yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add different content types like messaging, support docs, social posts, etc.
                    </p>
                    <Button variant="outline" onClick={handleAddContentItem}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Content Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaign.content_items.map((item) => {
                      const typeConfig = CONTENT_TYPES.find(t => t.value === item.content_type) || CONTENT_TYPES[0];
                      const Icon = typeConfig.icon;
                      return (
                        <div 
                          key={item.id} 
                          className="flex items-start justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className="p-2 bg-slate-100 rounded-lg">
                              <Icon className="h-5 w-5 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{item.title}</h4>
                                <Badge variant="secondary" className="text-xs">{typeConfig.label}</Badge>
                                {item.channel && (
                                  <Badge variant="outline" className="text-xs">{item.channel}</Badge>
                                )}
                                {item.variant && (
                                  <Badge variant="outline" className="text-xs">Variant: {item.variant}</Badge>
                                )}
                              </div>
                              {item.body && (
                                <p className="text-sm text-muted-foreground line-clamp-2">{item.body}</p>
                              )}
                              {item.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">{item.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleEditContentItem(item)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteContentItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Campaign Assets
                  </CardTitle>
                  <CardDescription>Upload images, videos, and documents for your campaign</CardDescription>
                </div>
                <Button onClick={() => setAssetDialog(true)} data-testid="add-asset-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Add Asset
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {campaign.assets.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No assets uploaded</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload images, videos, or documents for your campaign
                  </p>
                  <Button variant="outline" onClick={() => setAssetDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Asset
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {campaign.assets.map((asset) => (
                    <div key={asset.id} className="border rounded-lg overflow-hidden group">
                      {/* Asset Preview */}
                      <div className="aspect-video bg-slate-100 relative">
                        {asset.type === 'image' && asset.url ? (
                          <img
                            src={asset.url}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {asset.type === 'video' && <Video className="h-12 w-12 text-slate-400" />}
                            {asset.type === 'document' && <FileText className="h-12 w-12 text-slate-400" />}
                            {asset.type === 'creative' && <Palette className="h-12 w-12 text-slate-400" />}
                          </div>
                        )}
                        
                        {/* Hover Actions */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {asset.url && (
                            <Button
                              variant="secondary"
                              size="icon"
                              onClick={() => window.open(asset.url, '_blank')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => removeAsset(asset.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Asset Info */}
                      <div className="p-3">
                        <p className="font-medium text-sm truncate">{asset.name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <Badge variant="outline" className="text-xs capitalize">{asset.type}</Badge>
                          {asset.file_size && (
                            <span className="text-xs text-muted-foreground">
                              {(asset.file_size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Asset Upload Dialog */}
      <Dialog open={assetDialog} onOpenChange={setAssetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Asset</DialogTitle>
            <DialogDescription>Upload a file or add a URL</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Asset Name</Label>
              <Input
                placeholder="Enter asset name..."
                value={newAsset.name}
                onChange={(e) => setNewAsset(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Asset Type</Label>
              <Select
                value={newAsset.type}
                onValueChange={(v) => setNewAsset(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Upload File</Label>
              <Input
                type="file"
                accept={ASSET_TYPES.find(t => t.value === newAsset.type)?.accept}
                onChange={(e) => setNewAsset(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
              />
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>External URL</Label>
              <Input
                type="url"
                placeholder="https://..."
                value={newAsset.url}
                onChange={(e) => setNewAsset(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Describe this asset..."
                value={newAsset.description}
                onChange={(e) => setNewAsset(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssetUpload} disabled={uploadingAsset}>
              {uploadingAsset ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Add Asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Item Dialog */}
      <Dialog open={contentItemDialog} onOpenChange={setContentItemDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingContentItem ? 'Edit Content Item' : 'Add Content Item'}</DialogTitle>
            <DialogDescription>Create different content types for your campaign</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type *</Label>
                <Select
                  value={newContentItem.content_type}
                  onValueChange={(v) => setNewContentItem(prev => ({ ...prev, content_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {CONTENT_TYPES.find(t => t.value === newContentItem.content_type)?.description}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Channel</Label>
                <Select
                  value={newContentItem.channel}
                  onValueChange={(v) => setNewContentItem(prev => ({ ...prev, channel: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select channel..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific channel</SelectItem>
                    {CHANNELS.map((channel) => (
                      <SelectItem key={channel.value} value={channel.value}>
                        {channel.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="e.g., Welcome Email Copy, Product Launch Blog Post"
                value={newContentItem.title}
                onChange={(e) => setNewContentItem(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Content Body</Label>
              <Textarea
                placeholder="Enter your content here... This could be email copy, blog post content, social media text, etc."
                value={newContentItem.body}
                onChange={(e) => setNewContentItem(prev => ({ ...prev, body: e.target.value }))}
                rows={6}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Variant (optional)</Label>
                <Input
                  placeholder="e.g., A, B, Version 2, Short form"
                  value={newContentItem.variant}
                  onChange={(e) => setNewContentItem(prev => ({ ...prev, variant: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Use for A/B testing or content variations</p>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Internal notes about this content"
                  value={newContentItem.notes}
                  onChange={(e) => setNewContentItem(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setContentItemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveContentItem}>
              <Plus className="h-4 w-4 mr-2" />
              {editingContentItem ? 'Save Changes' : 'Add Content'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Content Generator */}
      <AIContentGenerator
        open={showAIGenerator}
        onOpenChange={setShowAIGenerator}
        onSelectContent={handleAIContentSelect}
        initialType={aiGeneratorField}
        context={{
          campaign_name: campaign.name,
          campaign_type: campaign.campaign_type,
          description: campaign.description,
          channels: campaign.channels,
          target_segments: campaign.target_segments
        }}
      />
    </div>
  );
};

export default CampaignEditPage;
